// Pure-function picker view-model builder.
//
// Some browsers expose the same physical Bluetooth device twice — once under
// its real device ID and once under a generic groupId-derived ID. The picker
// UI must dedup or the user sees ghost duplicates. The view-model also
// captures whether the call is live: routes can only be changed mid-call, so
// the picker is rendered disabled before the call has started and after it
// has ended. Like the policy file, every input is locally-derived and no
// peer / server string ever reaches the UI.
//
// Mirrors iOS AudioRouteAvailabilityCalculator.swift exactly.

import type { AudioRouteDescriptor, AudioSessionSnapshot } from './AudioRoute';
import type { AudioRouteSelection } from './AudioRoutePolicy';

/**
 * Display state for the audio-route picker UI.
 *
 * `routes`        : Deduplicated and order-preserved list of routes to show.
 * `currentRouteId`: Stable ID for the currently-active route; matches either
 *                   `portIdentifier` (for physical devices) or `kind` (for
 *                   system-virtual routes). The UI uses this to highlight the
 *                   active item without doing its own lookup.
 * `canChangeRoute`: False before the call goes active and after it ends. The
 *                   picker rows must be disabled (not just hidden) when false
 *                   so the user cannot issue a route change that would be
 *                   ignored by the adapter.
 */
export interface AudioRoutePickerViewModel {
  readonly routes: readonly AudioRouteDescriptor[];
  readonly currentRouteId: string;
  readonly canChangeRoute: boolean;
}

/**
 * Builds the picker's display state.
 *
 * Deduplicates routes by `portIdentifier` while preserving order (first
 * occurrence wins). Routes with no `portIdentifier` (earpiece, speakerphone)
 * are kept as-is because they have no opaque ID to compare.
 *
 * @param snapshot         Current audio system snapshot.
 * @param currentSelection The route currently in effect (possibly locked by user).
 * @param callIsActive     True while the call is in the active/connected state.
 */
export function buildPickerViewModel(
  snapshot: AudioSessionSnapshot,
  currentSelection: AudioRouteSelection,
  callIsActive: boolean,
): AudioRoutePickerViewModel {
  const routes = dedupByPortIdentifier(snapshot.availableRoutes);
  const currentRouteId =
    currentSelection.chosen.portIdentifier ?? currentSelection.chosen.kind;
  return {
    routes,
    currentRouteId,
    canChangeRoute: callIsActive,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Removes duplicate routes that share the same non-undefined portIdentifier.
 * Routes with `portIdentifier === undefined` (earpiece/speakerphone) are
 * always kept because they have no opaque UID to compare.
 *
 * Mirrors iOS `AudioRouteAvailabilityCalculator.dedupByPortIdentifier(_:)`.
 */
function dedupByPortIdentifier(
  routes: readonly AudioRouteDescriptor[],
): AudioRouteDescriptor[] {
  const seen = new Set<string>();
  const out: AudioRouteDescriptor[] = [];
  for (const r of routes) {
    if (r.portIdentifier !== undefined) {
      if (seen.has(r.portIdentifier)) continue;
      seen.add(r.portIdentifier);
    }
    out.push(r);
  }
  return out;
}
